/**
 * Loading the glyph atlas, and choosing a renderer.
 *
 * The atlas is a committed PNG in `@annwn/content`, imported as a URL so Vite
 * resolves, hashes and fingerprints it like any other asset. Metadata comes
 * from the same package as typed data, generated alongside the image, so the
 * two cannot disagree about how many columns there are.
 */

import { GLYPH_ATLAS, GLYPH_CELL } from '@annwn/content';
import atlasUrl from '@annwn/content/assets/annwn-16.png';

import { CanvasRenderer } from './canvas-renderer.js';
import type { GlyphAtlas, Renderer } from './renderer.js';
import { WebGLRenderer } from './webgl-renderer.js';

export async function loadAtlas(url: string = atlasUrl): Promise<GlyphAtlas> {
  const image = new Image();
  image.src = url;

  await image.decode();

  return {
    image,
    columns: GLYPH_ATLAS.columns,
    rows: GLYPH_ATLAS.rows,
    cellWidth: GLYPH_CELL.width,
    cellHeight: GLYPH_CELL.height,
  };
}

/**
 * WebGL2 if we can have it, Canvas2D if we cannot.
 *
 * The fallback is taken on *any* failure to construct the GL renderer, not
 * just on a missing context: a driver that reports WebGL2 and then fails to
 * compile a shader is a real configuration, and it should end up with a
 * playable game and a line in the console rather than a blank canvas.
 */
export function createRenderer(canvas: HTMLCanvasElement, atlas: GlyphAtlas): Renderer {
  try {
    return new WebGLRenderer(canvas, atlas);
  } catch (error) {
    console.warn('[render] falling back to Canvas2D:', error);
    return new CanvasRenderer(canvas, atlas);
  }
}
